import { useNotification } from "hooks/useNotification";
import {
  useMutation,
  useQuery,
  useQueryClient,
  queryOptions,
} from "@tanstack/react-query";
import {
  booleanMonthListSchema,
  PatchSeparatorDTO,
  Separator,
  SeparatorType,
  UpdateSeparatorDTO,
} from "types/separator/separator.type";
import {
  FacilityType,
  WaterMeasurementLength,
  WaterMeasurementPoint,
  WaterMeasurementReportingUnit,
  WaterMeasurementVehicleType,
  WaterMeterExists,
} from "types/separator/waterMeasurementPointData.type";
import instance from "./axiosInstance";
import {
  FileDTO,
  FiveYearsInspectionQueryKey,
} from "./fiveYearsInspection.api";
import { SixMonthsInspectionQueryKey } from "./sixMonthsInspection.api";
import { z } from "zod";

export const ENDPOINT = "/separator";
export enum SeparatorQueryKey {
  GetOne = "separator-get-one",
  GetSeparatorAssignments = "separator-get-assignments",
}

export const getSeparator = (id: number) =>
  queryOptions({
    queryKey: [SeparatorQueryKey.GetOne, id],
    queryFn: async (): Promise<Separator> =>
      (await instance.get(`${ENDPOINT}/${id}`)).data,
  });

export const useGetSeparator = (id: number, options = {}) =>
  useQuery({
    ...getSeparator(id),
    ...options,
  });

export const separatorMetadataSchema = z
  .object({
    type: z.nativeEnum(SeparatorType),
    identifier: z.string().min(1, "Obligatoriskt fält"),
    fritextType: z.string().optional(),
    notes: z.string().optional(),
    coordinates: z.string().optional(),
    propertyDesignation: z.string().optional(),
    avtalFemarskontroll: z.boolean().optional().default(false),
    avtalHalvarsKontroll: z.boolean().optional().default(false),
    avtalSlamtomning: z.boolean().optional().default(false),
    avtalVattenprovtagning: z.boolean().optional().default(false),
    ignoredHalvarsKontrollDate: z.string().nullable().optional(),
    ignoredFemarsKontrollDate: z.string().nullable().optional(),
    ignoredTomningDate: z.string().nullable().optional(),
    tomningReminder: booleanMonthListSchema.optional(),
    waterMeasurementPointData: z
      .object({
        waterMeasurementLength: z
          .nativeEnum(WaterMeasurementLength)
          .nullable()
          .optional(),
        measurementPoint: z
          .nativeEnum(WaterMeasurementPoint)
          .nullable()
          .optional(),
        analyses: z.string().nullable().optional(),
        limits: z.string().nullable().optional(),
        facilityType: z.nativeEnum(FacilityType).nullable().optional(),
        facilityTypeOther: z.string().nullable().optional(),
        waterMeterExists: z.nativeEnum(WaterMeterExists).nullable().optional(),
        reporting: z
          .nativeEnum(WaterMeasurementReportingUnit)
          .nullable()
          .optional(),
        vehicleType: z
          .nativeEnum(WaterMeasurementVehicleType)
          .nullable()
          .optional(),
      })
      .optional(),
    vattenprovtagningMonths: booleanMonthListSchema.optional(),
  })
  .refine(
    (data) => {
      if (data.type === SeparatorType.Fritext) {
        return data.fritextType && data.fritextType.length > 0;
      }
      return true;
    },
    {
      message: "Obligatoriskt fält",
      path: ["fritextType"],
    },
  )
  .refine(
    (data) => {
      if (data.avtalSlamtomning) {
        // Check if at least one month is selected
        const months = data.tomningReminder;
        if (!months) return false;

        return (
          months.january ||
          months.february ||
          months.march ||
          months.april ||
          months.may ||
          months.june ||
          months.july ||
          months.august ||
          months.september ||
          months.october ||
          months.november ||
          months.december
        );
      }
      return true;
    },
    {
      message: "Välj minst en månad för tömning",
      path: ["tomningReminder"],
    },
  );

export type TSeparatorMetadataSchema = z.infer<typeof separatorMetadataSchema>;

export type CreateSeparatorData = {
  facilityUuid: string;
  payload: TSeparatorMetadataSchema;
};

export const createSeparator = () => ({
  mutationFn: async ({
    payload,
    facilityUuid,
  }: CreateSeparatorData): Promise<Separator> =>
    (await instance.post(ENDPOINT, { facilityUuid, ...payload })).data,
});

export type PatchSeparatorData = {
  separatorId: number;
  payload: PatchSeparatorDTO;
};

export const patchSeparator = () => ({
  mutationFn: async ({
    separatorId,
    payload,
  }: PatchSeparatorData): Promise<Separator> =>
    (await instance.patch(`${ENDPOINT}/${separatorId}`, payload)).data,
});

export const useUpdateSeparator = (id: number, settings = {}) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: UpdateSeparatorDTO): Promise<Separator> => {
      const response = await instance.put(`${ENDPOINT}/${id}`, payload);
      return response.data;
    },
    onSuccess: (data: Separator) => {
      notification.info(`Ändringarna sparades`);
      queryClient.invalidateQueries({
        queryKey: [SeparatorQueryKey.GetOne, data.id],
      });
      queryClient.invalidateQueries({
        queryKey: [SeparatorQueryKey.GetSeparatorAssignments, data.id],
      });
      queryClient.invalidateQueries({
        queryKey: [FiveYearsInspectionQueryKey.GetOne],
      });
      queryClient.invalidateQueries({
        queryKey: [SixMonthsInspectionQueryKey.GetOne],
      });
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
    ...settings,
  });
};

export const useDeleteSeparator = (id: number) => {
  const notification = useNotification();

  return useMutation({
    mutationFn: async () => {
      return instance.delete(`${ENDPOINT}/${id}`);
    },
    onSuccess: () => {
      notification.info(`Objekt borttaget`);
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
  });
};

export const useSetSeparatorFiles = (options = {}) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation({
    mutationFn: async (payload: SeparatorFilesDTO): Promise<Separator> => {
      const response = await instance.put(ENDPOINT + "/files", payload);
      return response.data;
    },
    onError: () => {
      notification.error("Något gick fel, kontakta support");
    },
    onSuccess: (data: Separator) => {
      notification.info("Ändringarna sparade");
      queryClient.invalidateQueries({
        queryKey: [SeparatorQueryKey.GetOne, data.id],
      });
    },
    ...options,
  });
};

export type SeparatorFilesDTO = {
  separatorId: number;
  files: FileDTO[];
};
